
import { BulkUploadModal, ValidationError, BulkUploadResult, LanguageColumnMapping } from '@/components/common/BulkUploadModal';
import { Block, Floor, ApartmentUnit, Parking } from '@/types/resources/property';
import { useAuth } from '@/contexts/AuthContext';
import { addBlock, addApartmentUnitsBulk, addParkingBulk } from '@/api/properties';
import { message } from 'antd';
import { useTranslation } from 'react-i18next';
import { getTemplateURL } from '@/templates/csv/templateLoader';
import { Modal, Progress, Button, Row, Col, Typography } from 'antd';
import { incrementBlockCount, initializeBlockStats } from '@/api/workspaces';
import { useState } from 'react';

const { Title, Text } = Typography;

interface BlockBulkUploadModalProps {
  visible: boolean;
  onCancel: () => void;
  onSuccess: () => void;
}

export function BlockBulkUploadModal({ visible, onCancel, onSuccess }: BlockBulkUploadModalProps) {
  const { firestoreUser, refreshWorkspaceStats } = useAuth();
  const { t, i18n } = useTranslation(['properties', 'common']);
  const currentLanguage = i18n.language as 'en' | 'es' | 'pt';
  
  // State for managing the flow
  const [currentStep, setCurrentStep] = useState<'upload' | 'preview' | 'creating'>('upload');
  const [processedData, setProcessedData] = useState<Array<{
    block: Omit<Block, 'id' | 'workspace' | 'activityRecords' | 'images'> & { 
      unitsPerFloor: number; 
      createParking: string | boolean 
    };
    floors: Floor[];
    totalUnits: number;
    totalParkings: number;
  }>>([]);

  // Sample data for template generation (will be replaced by CSV template)
  const sampleData: any[] = [];

  // Column definitions for the template (internationalized) - matching BlockFormModal
  const columns = [
    { key: 'name', title: t('properties:blocks.fields.name'), required: true },
    { key: 'description', title: t('properties:blocks.fields.description'), required: true },
    { key: 'numberOfFloors', title: t('properties:blocks.fields.numberOfFloors'), required: true },
    { key: 'unitsPerFloor', title: t('properties:blocks.fields.unitsPerFloor'), required: true },
    { key: 'floorPrefix', title: t('properties:blocks.fields.floorPrefix'), required: false },
    { key: 'createParking', title: t('properties:blocks.fields.createParking'), required: false }
  ];

  // Language-aware column mappings for CSV headers
  const columnMappings: LanguageColumnMapping = {
    en: {
      name: ['name', 'block name', 'blockname', 'block_name'],
      description: ['description', 'desc', 'block description', 'blockdescription'],
      numberOfFloors: ['numberoffloors', 'number of floors', 'floors', 'total floors', 'floor count', 'floorcount', 'totalfloors'],
      unitsPerFloor: ['unitsperfloor', 'units per floor', 'units', 'apartments per floor', 'apartmentsperfloor'],
      floorPrefix: ['floorprefix', 'floor prefix', 'prefix', 'floorpref', 'floor_pref', 'unit prefix', 'unitprefix'],
      createParking: ['createparking', 'create parking', 'parking', 'include parking', 'includeparking', 'parking spaces']
    },
    es: {
      name: ['nombre', 'name', 'nombre del bloque', 'nombredelbloque', 'bloque'],
      description: ['descripción', 'descripcion', 'description', 'desc', 'descripción del bloque', 'descripcion del bloque'],
      numberOfFloors: ['númerodepisos', 'numerodepisos', 'número de pisos', 'numero de pisos', 'pisos', 'total de pisos', 'totaldepisos', 'cantidad de pisos'],
      unitsPerFloor: ['unidadesporpiso', 'unidades por piso', 'unidades', 'apartamentos por piso', 'apartamentosporpiso'],
      floorPrefix: ['prefijodepiso', 'prefijo de piso', 'prefijo', 'prefix', 'prefijopiso', 'prefijo_piso', 'prefijo de unidad'],
      createParking: ['crearestacionamiento', 'crear estacionamiento', 'estacionamiento', 'incluir estacionamiento', 'incluirestacionamiento', 'espacios de estacionamiento']
    },
    pt: {
      name: ['nome', 'name', 'nome do bloco', 'nomedobloco', 'bloco'],
      description: ['descrição', 'descricao', 'description', 'desc', 'descrição do bloco', 'descricao do bloco'],
      numberOfFloors: ['númerodeandares', 'numerodeandares', 'número de andares', 'numero de andares', 'andares', 'total de andares', 'totaldeandares', 'quantidade de andares'],
      unitsPerFloor: ['unidadesporandar', 'unidades por andar', 'unidades', 'apartamentos por andar', 'apartamentosporandar'],
      floorPrefix: ['prefixodoandar', 'prefixo do andar', 'prefixo', 'prefix', 'prefixoandar', 'prefixo_andar', 'prefixo da unidade'],
      createParking: ['criarestacionamento', 'criar estacionamento', 'estacionamento', 'incluir estacionamiento', 'incluirestacionamento', 'vagas de estacionamento']
    }
  };

  // Generate floors using the same logic as BlockFormModal
  const generateFloors = (blockName: string, prefix: string, totalFloors: number, unitsPerFloor: number): Floor[] => {
    return Array.from({ length: totalFloors }, (_, index) => {
      const floorNumber = index + 1;
      const name = prefix ? `${prefix}${floorNumber}` : `${floorNumber}`;
      const slug = `${blockName.toLowerCase()}-${prefix ? prefix.toLowerCase() : ''}${floorNumber}`;
      
      // Generate apartment units for this floor
      const apartmentUnits = Array.from({ length: unitsPerFloor }, (_, unitIndex) => {
        const unitNumber = unitIndex + 1;
        const apartmentUnitNumber = `${floorNumber}${unitNumber}`; // e.g., 11, 12, 13, 14 for floor 1
        
        return {
          number: apartmentUnitNumber,
          name: `Unit ${apartmentUnitNumber}`,
          slug: `${slug}-unit-${apartmentUnitNumber}`
        };
      });
      
      return {
        name,
        slug,
        description: '',
        apartmentUnits
      };
    });
  };

  // Validation function for block records - matching BlockFormModal validation
  const validateBlockRecord = (record: any, rowIndex: number): ValidationError[] => {
    const errors: ValidationError[] = [];

    // Required field validation
    if (!record.name || record.name.trim() === '') {
      errors.push({
        row: rowIndex,
        field: 'name',
        value: record.name,
        message: t('properties:blocks.bulkUpload.validation.nameRequired')
      });
    } else if (record.name.length > 100) {
      errors.push({
        row: rowIndex,
        field: 'name',
        value: record.name,
        message: t('properties:blocks.bulkUpload.validation.nameTooLong')
      });
    }

    if (!record.description || record.description.trim() === '') {
      errors.push({
        row: rowIndex,
        field: 'description',
        value: record.description,
        message: t('properties:blocks.bulkUpload.validation.descriptionRequired')
      });
    } else if (record.description.length > 500) {
      errors.push({
        row: rowIndex,
        field: 'description',
        value: record.description,
        message: t('properties:blocks.bulkUpload.validation.descriptionTooLong')
      });
    }

    // Number of floors validation (required field)
    if (!record.numberOfFloors || record.numberOfFloors === '') {
      errors.push({
        row: rowIndex,
        field: 'numberOfFloors',
        value: record.numberOfFloors,
        message: t('properties:blocks.bulkUpload.validation.numberOfFloorsRequired')
      });
    } else {
      const numberOfFloors = parseInt(record.numberOfFloors);
      if (isNaN(numberOfFloors) || numberOfFloors < 1 || numberOfFloors > 100) {
        errors.push({
          row: rowIndex,
          field: 'numberOfFloors',
          value: record.numberOfFloors,
          message: t('properties:blocks.bulkUpload.validation.numberOfFloorsInvalid')
        });
      }
    }

    // Units per floor validation (required field)
    if (!record.unitsPerFloor || record.unitsPerFloor === '') {
      errors.push({
        row: rowIndex,
        field: 'unitsPerFloor',
        value: record.unitsPerFloor,
        message: t('properties:blocks.bulkUpload.validation.unitsPerFloorRequired')
      });
    } else {
      const unitsPerFloor = parseInt(record.unitsPerFloor);
      if (isNaN(unitsPerFloor) || unitsPerFloor < 1 || unitsPerFloor > 50) {
        errors.push({
          row: rowIndex,
          field: 'unitsPerFloor',
          value: record.unitsPerFloor,
          message: t('properties:blocks.bulkUpload.validation.unitsPerFloorInvalid')
        });
      }
    }

    // Floor prefix validation (optional)
    if (record.floorPrefix !== undefined && record.floorPrefix !== '') {
      if (record.floorPrefix.length > 10) {
        errors.push({
          row: rowIndex,
          field: 'floorPrefix',
          value: record.floorPrefix,
          message: t('properties:blocks.bulkUpload.validation.floorPrefixTooLong')
        });
      }
    }

    // Create parking validation (optional boolean)
    if (record.createParking !== undefined && record.createParking !== '') {
      const createParking = record.createParking.toString().toLowerCase();
      if (!['true', 'false', '1', '0', 'yes', 'no'].includes(createParking)) {
        errors.push({
          row: rowIndex,
          field: 'createParking',
          value: record.createParking,
          message: t('properties:blocks.bulkUpload.validation.createParkingInvalid')
        });
      }
    }

    return errors;
  };

  // Transform CSV record to Block type and generate floors automatically - matching BlockFormModal
  const transformBlockRecord = (record: any): Omit<Block, 'id' | 'workspace' | 'activityRecords' | 'images'> & { 
    unitsPerFloor: number; 
    createParking: string | boolean 
  } => {
    const numberOfFloors = parseInt(record.numberOfFloors); // Required field, already validated
    const unitsPerFloor = parseInt(record.unitsPerFloor); // Required field, already validated
    const floorPrefix = record.floorPrefix || '';
    
    // Generate floors using the same logic as BlockFormModal
    const floors = generateFloors(record.name.trim(), floorPrefix, numberOfFloors, unitsPerFloor);

    return {
      name: record.name.trim(),
      description: record.description.trim(),
      floors,
      unitsPerFloor,
      createParking: record.createParking || false
    };
  };

  // Handle bulk upload confirmation - now shows preview first
  const handleBulkUploadConfirm = async (result: BulkUploadResult<Omit<Block, 'id' | 'workspace' | 'activityRecords' | 'images'> & { 
    unitsPerFloor: number; 
    createParking: string | boolean 
  }>) => {
    if (!firestoreUser) {
      message.error('User not authenticated');
      return;
    }

    try {
      // Process the data to show preview
      const processedData = result.validRecords.map(blockData => {
        const totalUnits = (blockData.floors?.length || 0) * blockData.unitsPerFloor;
        const createParking = blockData.createParking && (blockData.createParking === 'true' || blockData.createParking === '1' || blockData.createParking === 'yes');
        const totalParkings = createParking ? totalUnits : 0;

        return {
          block: blockData,
          floors: blockData.floors || [],
          totalUnits,
          totalParkings
        };
      });

      setProcessedData(processedData);
      setCurrentStep('preview');

    } catch (error) {
      console.error('Error processing data:', error);
      message.error('Error processing data. Please try again.');
    }
  };

  // Handle the actual creation after confirmation
  const handleConfirmCreation = async () => {
    if (!firestoreUser || processedData.length === 0) {
      message.error('No data to process');
      return;
    }

    try {
      setCurrentStep('creating');
      
      let totalUploaded = 0;
      let totalApartmentUnits = 0;
      let totalParkings = 0;
      const totalRecords = processedData.length;
      
      // Show progress modal
      const progressModal = Modal.info({
        title: 'Creating Blocks with Apartment Units and Parking',
        content: (
          <div>
            <Progress 
              percent={0} 
              status="active" 
              format={(percent) => `${totalUploaded}/${totalRecords} (${percent}%)`}
            />
            <p>Creating blocks one by one...</p>
            <p>This will also create apartment units and parking spaces automatically.</p>
          </div>
        ),
        okButtonProps: { style: { display: 'none' } }
      });

      // Process each block individually to get the block ID for creating units and parking
      for (let blockIndex = 0; blockIndex < processedData.length; blockIndex++) {
        const data = processedData[blockIndex];
        const blockData = data.block;
        
        try {
          // Step 1: Create the block (without floors to avoid duplication)
          const blockWithoutFloors = {
            name: blockData.name,
            description: blockData.description
          };

          progressModal.update({
            content: (
              <div>
                <Progress 
                  percent={Math.round((blockIndex / totalRecords) * 100)} 
                  status="active" 
                  format={(percent) => `${blockIndex + 1}/${totalRecords} (${percent}%)`}
                />
                <p>Creating block: {blockData.name}</p>
                <p>Progress: {blockIndex + 1} of {totalRecords}</p>
              </div>
            )
          });

          // Create block using the existing API (this will return the block ID)
          const blockId = await addBlock(blockWithoutFloors, firestoreUser);

          if (!blockId) {
            throw new Error(`Failed to create block: ${blockData.name}`);
          }

          totalUploaded++;
          
          // Step 2: Create apartment units for this block
          const apartmentUnits: Omit<ApartmentUnit, 'id' | 'workspace' | 'activityRecords'>[] = [];
          const parkingUnits: Omit<Parking, 'id' | 'workspace' | 'activityRecords'>[] = [];
          
          if (data.floors) {
            data.floors.forEach(floor => {
              floor.apartmentUnits?.forEach(unit => {
                // Add apartment unit
                apartmentUnits.push({
                  number: unit.number,
                  status: 'vacant',
                  block: {
                    id: blockId,
                    name: blockData.name
                  },
                  floor: {
                    name: floor.name,
                    slug: floor.slug
                  },
                  description: `Unit ${unit.number} on ${floor.name}`
                });

                // Add parking unit if createParking is true
                const createParking = blockData.createParking;
                if (createParking && (createParking === 'true' || createParking === '1' || createParking === 'yes')) {
                  parkingUnits.push({
                    number: `${blockData.name}-${unit.number}`,
                    type: 'car',
                    status: 'available',
                    block: {
                      id: blockId,
                      name: blockData.name
                    },
                    apartmentUnit: {
                      id: '', // Will be filled by the system
                      number: unit.number
                    },
                    description: `Parking for Unit ${unit.number} on ${floor.name}`
                  });
                }
              });
            });
          }

          // Create apartment units in bulk
          if (apartmentUnits.length > 0) {
            progressModal.update({
              content: (
                <div>
                  <Progress 
                    percent={Math.round((blockIndex / totalRecords) * 100)} 
                    status="active" 
                    format={(percent) => `${blockIndex + 1}/${totalRecords} (${percent}%)`}
                  />
                  <p>Creating apartment units for: {blockData.name}</p>
                  <p>Total units to create: {apartmentUnits.length}</p>
                </div>
              )
            });

            await addApartmentUnitsBulk(apartmentUnits, firestoreUser, () => {
              // Progress callback for apartment units
            });
            
            totalApartmentUnits += apartmentUnits.length;
          }

          // Create parking units in bulk
          if (parkingUnits.length > 0) {
            progressModal.update({
              content: (
                <div>
                  <Progress 
                    percent={Math.round((blockIndex / totalRecords) * 100)} 
                    status="active" 
                    format={(percent) => `${blockIndex + 1}/${totalRecords} (${percent}%)`}
                  />
                  <p>Creating parking spaces for: {blockData.name}</p>
                  <p>Total parking spaces to create: {parkingUnits.length}</p>
                </div>
              )
            });

            await addParkingBulk(parkingUnits, firestoreUser, () => {
              // Progress callback for parking units
            });
            
            totalParkings += parkingUnits.length;
          }

          // Step 3: Update workspace stats for this block
          const totalUnits = data.totalUnits;
          const createParking = blockData.createParking && (blockData.createParking === 'true' || blockData.createParking === '1' || blockData.createParking === 'yes');
          const totalParkingSpaces = createParking ? totalUnits : 0;

          try {
            // Initialize block stats
            await initializeBlockStats(firestoreUser, blockId, blockData.name);
            
            // Update block-specific stats
            await incrementBlockCount(firestoreUser, blockId, 'totalApartmentUnits', totalUnits);
            if (createParking) {
              await incrementBlockCount(firestoreUser, blockId, 'totalParkings', totalParkingSpaces);
            }
          } catch (error) {
            console.warn(`Failed to update stats for block ${blockId}:`, error);
          }

          // Update progress
          const percent = Math.round(((blockIndex + 1) / totalRecords) * 100);
          progressModal.update({
            content: (
              <div>
                <Progress 
                  percent={percent} 
                  status="active" 
                  format={(percent) => `${blockIndex + 1}/${totalRecords} (${percent}%)`}
                />
                <p>Completed: {blockData.name}</p>
                <p>Progress: {blockIndex + 1} of {totalRecords}</p>
                <p>Blocks: {totalUploaded}, Units: {totalApartmentUnits}, Parking: {totalParkings}</p>
              </div>
            )
          });

        } catch (error) {
          console.error(`Error processing block ${blockData.name}:`, error);
          message.error(`Error processing block ${blockData.name}. Continuing with next block...`);
        }
      }

      // Refresh workspace stats
      try {
        await refreshWorkspaceStats();
      } catch (error) {
        console.warn('Failed to refresh workspace stats:', error);
      }

      progressModal.destroy();
      message.success(`Successfully created ${totalUploaded} blocks with ${totalApartmentUnits} apartment units and ${totalParkings} parking spaces`);
      
      onSuccess();
    } catch (error) {
      console.error('Progress-aware upload error:', error);
      message.error('Error creating blocks. Please try again.');
    }
  };

  // Handle modal cancel
  const handleModalCancel = () => {
    setCurrentStep('upload');
    setProcessedData([]);
    onCancel();
  };

  // Render different content based on current step
  const renderContent = () => {
    switch (currentStep) {
      case 'upload':
        return (
          <BulkUploadModal
            visible={visible}
            onCancel={handleModalCancel}
            onConfirm={handleBulkUploadConfirm}
            title={t('properties:blocks.actions.bulkUpload')}
            templateUrl={getTemplateURL(currentLanguage, 'blocks')}
            templateFileName={`blocks_template_${currentLanguage}.csv`}
            validateRecord={validateBlockRecord}
            transformRecord={transformBlockRecord}
            columns={columns}
            sampleData={sampleData}
            accept=".csv"
            maxFileSize={5}
            language={currentLanguage}
            columnMappings={columnMappings}
          />
        );

      case 'preview':
        return (
          <Modal
            title={t('properties:blocks.bulkUpload.title')}
            open={visible}
            onCancel={handleModalCancel}
            footer={[
              <Button key="cancel" onClick={handleModalCancel}>
                {t('common:actions.cancel')}
              </Button>,
              <Button key="confirm" type="primary" onClick={handleConfirmCreation}>
                {t('common:actions.confirm')}
              </Button>
            ]}
            width={800}
          >
            <div style={{ marginTop: 16 }}>
              <div style={{ textAlign: 'center', marginBottom: '24px' }}>
                <Title level={3} style={{ color: '#1890ff', marginBottom: '8px' }}>
                  {t('properties:blocks.messages.creationSummary.title')}
                </Title>
                <Text style={{ color: '#666', fontSize: '14px' }}>
                  {t('properties:blocks.messages.creationSummary.subtitle')}
                </Text>
              </div>

              {/* Summary Details */}
              <div style={{ 
                backgroundColor: '#fff', 
                padding: '20px', 
                borderRadius: '6px', 
                border: '1px solid #d9d9d9',
                marginBottom: '24px'
              }}>
                {processedData.map((data, index) => (
                  <div key={index} style={{ 
                    marginBottom: index < processedData.length - 1 ? '20px' : '0',
                    padding: '16px',
                    backgroundColor: '#f8f9fa',
                    borderRadius: '6px',
                    border: '1px solid #e9ecef'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', marginBottom: '16px' }}>
                      <div style={{ 
                        width: '40px', 
                        height: '40px', 
                        backgroundColor: '#1890ff', 
                        borderRadius: '50%', 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'center',
                        marginRight: '16px'
                      }}>
                        <span style={{ color: '#fff', fontSize: '18px', fontWeight: 'bold' }}>
                          {data.block.name.charAt(0)?.toUpperCase() || 'B'}
                        </span>
                      </div>
                      <div>
                        <Title level={4} style={{ margin: 0, color: '#1890ff' }}>
                          {data.block.name}
                        </Title>
                        <Text style={{ margin: 0, color: '#666', fontSize: '12px' }}>
                          {data.block.description}
                        </Text>
                      </div>
                    </div>

                    <Row gutter={16}>
                      <Col span={6}>
                        <div style={{ textAlign: 'center', padding: '16px', backgroundColor: '#f6ffed', borderRadius: '6px', border: '1px solid #b7eb8f' }}>
                          <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#52c41a' }}>
                            {data.floors.length}
                          </div>
                          <div style={{ fontSize: '12px', color: '#52c41a' }}>
                            {t('properties:blocks.messages.creationSummary.floors')}
                          </div>
                        </div>
                      </Col>
                      <Col span={6}>
                        <div style={{ textAlign: 'center', padding: '16px', backgroundColor: '#e6f7ff', borderRadius: '6px', border: '1px solid #91d5ff' }}>
                          <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#1890ff' }}>
                            {data.block.unitsPerFloor}
                          </div>
                          <div style={{ fontSize: '12px', color: '#1890ff' }}>
                            {t('properties:blocks.messages.creationSummary.unitsPerFloor')}
                          </div>
                        </div>
                      </Col>
                      <Col span={6}>
                        <div style={{ textAlign: 'center', padding: '16px', backgroundColor: '#fff7e6', borderRadius: '6px', border: '1px solid #ffd591' }}>
                          <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#fa8c16' }}>
                            {data.totalUnits}
                          </div>
                          <div style={{ fontSize: '12px', color: '#fa8c16' }}>
                            {t('properties:blocks.messages.creationSummary.totalUnits')}
                          </div>
                        </div>
                      </Col>
                      <Col span={6}>
                        <div style={{ textAlign: 'center', padding: '16px', backgroundColor: data.totalParkings > 0 ? '#f6ffed' : '#f5f5f5', borderRadius: '6px', border: `1px solid ${data.totalParkings > 0 ? '#b7eb8f' : '#d9d9d9'}` }}>
                          <div style={{ fontSize: '24px', fontWeight: 'bold', color: data.totalParkings > 0 ? '#52c41a' : '#999' }}>
                            {data.totalParkings}
                          </div>
                          <div style={{ fontSize: '12px', color: data.totalParkings > 0 ? '#52c41a' : '#999' }}>
                            Parking
                          </div>
                        </div>
                      </Col>
                    </Row>

                    {/* Floor Plan Preview */}
                    <div style={{ marginTop: '16px' }}>
                      <Text strong style={{ display: 'block', marginBottom: '8px' }}>
                        {t('properties:blocks.details.attributes.floorPlan')}:
                      </Text>
                      <div style={{ 
                        display: 'flex', 
                        flexDirection: 'column-reverse',
                        gap: '2px',
                        border: '2px solid #d9d9d9',
                        borderRadius: '8px',
                        padding: '12px',
                        backgroundColor: '#fafafa',
                        maxHeight: '300px',
                        overflowY: 'auto'
                      }}>
                        {data.floors.map((floor, floorIndex) => (
                          <div key={floorIndex} style={{ 
                            display: 'flex', 
                            alignItems: 'center',
                            gap: '8px',
                            padding: '6px',
                            backgroundColor: '#fff',
                            borderRadius: '4px',
                            border: '1px solid #e8e8e8'
                          }}>
                            <div style={{ 
                              minWidth: '60px',
                              fontWeight: 'bold',
                              color: '#1890ff',
                              fontSize: '12px',
                              textAlign: 'center'
                            }}>
                              {floor.name}
                            </div>
                            
                            <div style={{ 
                              display: 'flex', 
                              gap: '2px',
                              flexWrap: 'wrap'
                            }}>
                              {floor.apartmentUnits?.map((unit, unitIndex) => (
                                <div key={unitIndex} style={{
                                  width: '30px',
                                  height: '24px',
                                  backgroundColor: '#f0f0f0',
                                  border: '1px solid #d9d9d9',
                                  borderRadius: '3px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  fontSize: '8px',
                                  fontWeight: '500',
                                  color: '#595959'
                                }}>
                                  {unit.number}
                                </div>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                ))}

                {/* Confirmation Message */}
                <div style={{ 
                  marginTop: '20px', 
                  padding: '16px', 
                  backgroundColor: '#fff2e8', 
                  borderRadius: '6px', 
                  border: '1px solid #ffbb96',
                  textAlign: 'center'
                }}>
                  <Text style={{ margin: 0, color: '#d46b08', fontSize: '14px' }}>
                    {t('properties:blocks.messages.creationSummary.confirmationMessage', {
                      name: `these ${processedData.length} blocks`,
                      floors: processedData.reduce((sum, data) => sum + data.floors.length, 0),
                      unitsPerFloor: processedData.reduce((sum, data) => sum + data.block.unitsPerFloor, 0) / processedData.length,
                      totalUnits: processedData.reduce((sum, data) => sum + data.totalUnits, 0),
                      parking: processedData.reduce((sum, data) => sum + data.totalParkings, 0)
                    })}
                  </Text>
                </div>
              </div>
            </div>
          </Modal>
        );

      case 'creating':
        return (
          <Modal
            title="Creating Blocks..."
            open={visible}
            onCancel={handleModalCancel}
            footer={null}
            width={600}
          >
            <div style={{ textAlign: 'center', padding: '20px' }}>
              <Progress type="circle" percent={100} status="active" />
              <Text style={{ display: 'block', marginTop: '16px' }}>
                Please wait while the blocks are being created...
              </Text>
            </div>
          </Modal>
        );

      default:
        return null;
    }
  };

  return renderContent();
}
